/**
 * SearchInput.tsx — Controlled search input with debounce
 *
 * Purpose:
 *   Provides a styled text input for filtering admin list views. Debounces
 *   the onChange callback to reduce unnecessary API calls while typing.
 *
 * Public API:
 *   - SearchInput  Controlled search input component
 *
 * @module packages/billing-admin/src/components/SearchInput
 * @since 2026-05-31 (AT-50.01 — Scaffold Admin Console UI Foundation)
 */
import React, { useEffect, useRef, useState } from 'react';

interface SearchInputProps {
  /** Current search query value (controlled). */
  value: string;
  /** Called with the debounced query string after the user stops typing. */
  onChange: (value: string) => void;
  /** Placeholder text shown when empty. */
  placeholder?: string;
  /** Debounce delay in ms (default: 300). */
  debounceMs?: number;
  /** Whether the input is disabled. */
  disabled?: boolean;
}

/**
 * SearchInput — debounced controlled search input.
 *
 * RATIONALE: Internal state tracks the raw input value so the UI updates
 * immediately, while the external onChange is debounced to avoid firing on
 * every keystroke.
 *
 * @since 2026-05-31 (AT-50.01)
 */
export default function SearchInput({
  value,
  onChange,
  placeholder = 'Search…',
  debounceMs = 300,
  disabled = false,
}: SearchInputProps): React.JSX.Element {
  const [localValue, setLocalValue] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ARCHITECTURE: Sync localValue when external value changes (e.g. reset)
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const next = e.target.value;
    setLocalValue(next);

    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onChange(next);
    }, debounceMs);
  }

  return (
    <div className="relative">
      <span className="absolute inset-y-0 left-4 flex items-center text-slate-400">
        <svg aria-hidden="true" viewBox="0 0 20 20" className="h-4 w-4 fill-current">
          <path fillRule="evenodd" d="M8.5 3a5.5 5.5 0 1 0 3.473 9.766l3.63 3.63a.75.75 0 1 0 1.06-1.06l-3.629-3.63A5.5 5.5 0 0 0 8.5 3Zm-4 5.5a4 4 0 1 1 8 0 4 4 0 0 1-8 0Z" clipRule="evenodd" />
        </svg>
      </span>
      <input
        type="search"
        value={localValue}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full rounded-2xl border border-slate-200 bg-white/90 py-3 pl-11 pr-4 text-sm text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:border-cyan-400 focus:outline-none focus:ring-4 focus:ring-cyan-100 disabled:opacity-50"
      />
    </div>
  );
}
